import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, Dog, Cat, Megaphone, Wrench } from 'lucide-react';
import CreatePSAForm from '../CreatePSAForm/CreatePSAForm';
import { openModal, closeModal } from '../../utils/modalUtils';

/**
 * Post type selection options
 */
const POST_TYPES = [
  {
    value: 'psa',
    label: 'public_service_announcement',
    defaultLabel: 'Public Service Announcement',
    description: 'psa_type_description',
    defaultDescription: 'Share important community information',
    icon: Megaphone,
    color: 'amber',
  },
  {
    value: 'lost-animal',
    label: 'lost_animal',
    defaultLabel: 'Lost Animal',
    description: 'lost_type_description',
    defaultDescription: 'Report a missing pet to get help finding them',
    icon: Dog,
    color: 'red',
  },
  {
    value: 'found-animal',
    label: 'found_animal',
    defaultLabel: 'Found Animal',
    description: 'found_type_description',
    defaultDescription: 'Report an animal you found to help reunite them',
    icon: Cat,
    color: 'green',
  },
  {
    value: 'pet-service',
    label: 'pet_service',
    defaultLabel: 'Pet Service',
    description: 'pet_service_type_description',
    defaultDescription: 'Offer pet-related services like walking, sitting, or grooming',
    icon: Wrench,
    color: 'purple',
  },
];

/**
 * Get color classes based on post type color
 */
const getColorClasses = (color, variant = 'icon') => {
  const colors = {
    amber: {
      icon: 'bg-amber-100 text-amber-600',
      border: 'border-amber-200 hover:border-amber-400 hover:bg-amber-50',
      badge: 'bg-amber-500 text-white',
    },
    blue: {
      icon: 'bg-blue-100 text-blue-600',
      border: 'border-blue-200 hover:border-blue-400 hover:bg-blue-50',
      badge: 'bg-blue-500 text-white',
    },
    red: {
      icon: 'bg-red-100 text-red-600',
      border: 'border-red-200 hover:border-red-400 hover:bg-red-50',
      badge: 'bg-red-500 text-white',
    },
    green: {
      icon: 'bg-green-100 text-green-600',
      border: 'border-green-200 hover:border-green-400 hover:bg-green-50',
      badge: 'bg-green-500 text-white',
    },
    purple: {
      icon: 'bg-purple-100 text-purple-600',
      border: 'border-purple-200 hover:border-purple-400 hover:bg-purple-50',
      badge: 'bg-purple-500 text-white',
    },
  };
  return colors[color]?.[variant] || '';
};

/**
 * Modal component for creating new posts from the Home page
 * Type selection first, then passes selected type to CreatePSAForm
 */
const CreatePostModal = ({ 
  isOpen, 
  onClose,
  onPostCreated,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  
  const [selectedType, setSelectedType] = useState(null);

  // Handle close - wrapped in useCallback to avoid stale closures
  const handleClose = useCallback(() => {
    setSelectedType(null);
    onClose();
  }, [onClose]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedType(null);
    }
  }, [isOpen]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (isOpen && !isAuthenticated) {
      handleClose();
      navigate('/login');
    }
  }, [isOpen, isAuthenticated, navigate, handleClose]);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
  };

  const handleBack = () => {
    setSelectedType(null);
  };

  const handleFormSuccess = () => {
    if (onPostCreated) {
      onPostCreated();
    }
    handleClose();
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Prevent body scroll when modal is open
  const scrollYRef = useRef(0);
  
  useEffect(() => {
    if (isOpen) {
      scrollYRef.current = openModal();
    } else {
      closeModal(scrollYRef.current);
    }
    return () => {
      closeModal(scrollYRef.current);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-0 md:p-4">
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white flex flex-col w-full h-full
                         md:w-full md:max-w-lg md:h-auto md:max-h-[90vh] md:rounded-2xl md:shadow-2xl"
              role="dialog"
              aria-modal="true"
              aria-labelledby="create-post-title"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-3 sm:px-4 py-3 sm:py-4 border-b border-gray-100 flex-shrink-0">
                {selectedType ? (
                  <button
                    onClick={handleBack}
                    className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 -ml-1 sm:-ml-2 rounded-full hover:bg-gray-100 transition-colors"
                    aria-label={t('go_back', 'Go Back')}
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                  </button>
                ) : (
                  <div className="w-9 sm:w-10" />
                )}
                <h2 id="create-post-title" className="text-base sm:text-lg font-semibold text-gray-900">
                  {selectedType 
                    ? t('create_post', 'Create Post')
                    : t('new_post', 'New Post')
                  }
                </h2>
                <button
                  onClick={handleClose}
                  className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 -mr-1 sm:-mr-2 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label={t('close', 'Close')}
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                {!selectedType ? (
                  // Type Selection - Clean card style
                  <div className="p-3 sm:p-4 space-y-2.5 sm:space-y-3">
                    <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                      {t('what_would_you_like_to_post', 'What would you like to post?')}
                    </p>
                    
                    {POST_TYPES.map((postType) => {
                      const Icon = postType.icon;
                      return (
                        <button
                          key={postType.value}
                          onClick={() => handleTypeSelect(postType.value)}
                          className={`flex items-center gap-3 sm:gap-4 w-full p-3 sm:p-4 rounded-xl border-2 
                                     text-left transition-all duration-200 hover:shadow-md
                                     ${getColorClasses(postType.color, 'border')}`}
                        >
                          <div className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex-shrink-0 ${getColorClasses(postType.color, 'icon')}`}>
                            <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                              {t(postType.label, postType.defaultLabel)}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-500 line-clamp-2">
                              {t(postType.description, postType.defaultDescription)}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  // Form - Pass the selected type directly
                  <div className="p-3 sm:p-4">
                    <CreatePSAForm 
                      initialType={selectedType}
                      onSuccess={handleFormSuccess}
                      isModal={true}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreatePostModal;
